import { Box, Chip, CircularProgress } from '@mui/material'
import { GRAY_COLORS, INFO_COLORS } from 'asterX-UI'
import StandardTable from 'asterX-UI/Table/StandardTable'
import { NotFound } from 'components/NotFound/NotFound'
import { useContractStore } from 'context/ContractStore'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { getFullName, getTypeName, getValidDate } from 'lib/utils'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActionModal from '../../asterX-UI/Modals/ActionModal'

const getTableDataByTab = (tab, data) => {
  if (!data) {
    return {
      columns: [],
      rows: []
    }
  }

  const tableData = {
    applications: {
      columns: ['№', 'Клиент', 'Продукт', 'Сумма займа', 'Дата создания', 'Статус'],
      rows: data.map(item => ({
        isBs: item?.isBs,
        row: [
          <Box>{item?.id || 'Нет данных'}</Box>,
          <Box>{item?.clientFullName || 'Нет данных'}</Box>,
          item?.creditProduct?.name || 'Нет данных',
          item?.loanAmount != null ? item.loanAmount.toString().thousandSeparator() : 'Нет данных',
          getValidDate(item?.createdDate, true),
          <Chip
            size="small"
            label={item?.status?.name || item?.status?.code || 'Нет данных'}
            variant="outlined"
            sx={{ borderRadius: '6px', fontSize: '12px' }}
          />
        ]
      }))
    },

    contracts: {
      columns: ['№', 'Заявка', 'Продукт', 'Сумма займа', 'Дата создания', 'Статус'],
      rows: data?.map(item => [
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.id || '-'}</Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.loanApplication?.id || '-'}</Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.loanApplication?.creditProduct?.name || '-'}</Box>,
        item?.loanApplication?.loanAmount?.toString()?.thousandSeparator() || '-',
        getValidDate(item?.loanApplication?.createdDate, true),
        <Chip
          size="small"
          label={item?.loanApplication?.status?.name || item?.loanApplication?.status?.code || 'Заявка авторизована'}
          variant="outlined"
          sx={{ borderRadius: '6px', fontSize: '12px' }}
        />
      ])
    },

    collaterals: {
      columns: ['Наименование', 'Залогодатель', 'Заявка', 'Залоговая стоимость'],
      rows: data?.map(item => [
        <Box sx={{ color: INFO_COLORS[700] }}>{getTypeName(item?.type)}</Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>
          {getFullName(
            item?.clientGuarantee?.firstName,
            item?.clientGuarantee?.lastName,
            item?.clientGuarantee?.patronymic
          )}
        </Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.loanApplicationId}</Box>,
        item?.collateralValue?.toString().thousandSeparator() || '-'
      ])
    }
  }

  return tableData[tab] || { columns: [], rows: [] }
}

export const TableTab = ({ label }) => {
  const {
    contractStore: { client },
    contractStoreFunc: { getBsClientApplications }
  } = useContractStore()
  const [listData, setListData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()
  const [tableData, setTableData] = useState([])

  const getLabel = key => {
    const labels = {
      Заявки: 'applications',
      Контракты: 'contracts',
      Обеспечение: 'collaterals',
      'История заявок': 'applications'
    }

    return labels[key] || key
  }

  const fetchDataList = async (fetchFunc, data, limit = 100, offset = 0) => {
    const requestData = {
      ...data,
      limit,
      offset: Math.max((offset - 1) * 10, 0)
    }
    try {
      setIsLoading(true)
      const response = await fetchFunc(requestData)
      const { list = [], countFiltered = 0 } = response?.data || {}
      setIsLoading(false)
      return { list, count: countFiltered }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }

    return { list: [], count: 0 }
  }

  const getApplicationsList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getApplicationsList, data, limit, offset)
  }

  const getCollateralsList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getCollateralsList, data, limit, offset)
  }

  const getContractList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getContractList, data, limit, offset)
  }

  const getData = async (data, limit, offset) => {
    const fetchData = {
      applications: () =>
        getApplicationsList({ ...(data || {}), ...(client?.iin ? { iin: client.iin } : {}) }, limit, offset),
      contracts: () => getContractList({ ...(data || {}), ...(client?.iin ? { iin: client.iin } : {}) }, limit, offset),
      collaterals: () =>
        getCollateralsList({ ...(data || {}), ...(client?.iin ? { iin: client.iin } : {}) }, limit, offset)
    }

    const fetchFunction = fetchData[getLabel(label)]

    if (fetchFunction) {
      const { list } = await fetchFunction()
      let mergedList = list
      if (getLabel(label) === 'applications' && client?.iin) {
        const bsApps = await getBsClientApplications(client.iin)
        const transformedBsApps = (bsApps || []).map(item => ({
          id: item.id,
          clientFullName: client ? getFullName(client.firstName, client.lastName, client.patronymic) : '-',
          creditProduct: { name: item.product },
          loanAmount: item.loanAmount,
          createdDate: item.createdDate,
          status: { name: item.status },
          isBs: true,
          comment: item.comment,
          collaterals: item.collaterals
        }))
        mergedList = [...transformedBsApps, ...list]
      }

      setListData(mergedList)
    }
  }

  const handleRowClick = row => {
    const idCell = row.find(
      cell =>
        typeof cell?.props?.children === 'number' ||
        (typeof cell?.props?.children === 'string' && /^\d+$/.test(cell?.props?.children))
    )
    const id = idCell?.props?.children
    if (!id) return

    const item = listData.find(e => e.id === id)
    if (!item) return

    if (item.isBs) {
      setSelectedItem(item)
      setIsModalOpen(true)
    } else {
      navigate(`/application/${id}?readonly=true`)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }

  useEffect(() => {
    getData()
  }, [label, client?.iin])

  useEffect(() => {
    setTableData(getTableDataByTab(getLabel(label), listData))
  }, [listData])

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {listData?.length > 0 ? (
            <>
              <StandardTable
                tableData={tableData}
                tableStyles={{ border: `1px solid ${GRAY_COLORS[200]}` }}
                btnStyle={{ padding: 0, border: 'none' }}
                onRowClick={handleRowClick}
              />
              <ActionModal
                open={isModalOpen}
                onClose={closeModal}
                title={'Заявка от BrainySoft №' + selectedItem?.id}
                maxWidth="md"
              >
                {selectedItem && (
                  <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Chip
                      label={selectedItem.status?.name || 'Нет данных'}
                      sx={{ color: 'error.main', fontWeight: 500, width: 'fit-content' }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: '45%' }}>
                        <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>№</Box>
                        <Box>{selectedItem.id ?? 'Нет данных'}</Box>
                      </Box>
                      <Box sx={{ minWidth: '45%' }}>
                        <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>Продукт</Box>
                        <Box>{selectedItem.creditProduct?.name || 'Нет данных'}</Box>
                      </Box>
                      <Box sx={{ mt: 2, minWidth: '45%' }}>
                        <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>Сумма займа</Box>
                        <Box>
                          {selectedItem.loanAmount != null
                            ? selectedItem.loanAmount.toString().thousandSeparator()
                            : 'Нет данных'}
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2, minWidth: '45%' }}>
                        <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>Дата создания</Box>
                        <Box>{getValidDate(selectedItem.createdDate, true)}</Box>
                      </Box>
                    </Box>

                    <Box>
                      <Box sx={{ fontSize: 16, color: GRAY_COLORS[600], mb: 0.5 }}>Комментарий</Box>
                      <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedItem.comment?.trim() ? selectedItem.comment : 'Нет данных'}
                      </Box>
                    </Box>

                    <Box>
                      <Box sx={{ fontWeight: 600, mb: 1, fontSize: 18 }}>Залоги</Box>
                      {selectedItem.collaterals?.length > 0 ? (
                        selectedItem.collaterals.map((collateral, idx) => (
                          <Box key={idx} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                              <Box sx={{ minWidth: '45%' }}>
                                <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>Дилер</Box>
                                <Box>{collateral.dealership || 'Нет данных'}</Box>
                              </Box>
                              <Box sx={{ minWidth: '45%' }}>
                                <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>Первоначальный взнос</Box>
                                <Box>
                                  {collateral.initialPayment != null
                                    ? new Intl.NumberFormat().format(collateral.initialPayment)
                                    : 'Нет данных'}
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ fontSize: 16, color: GRAY_COLORS[600] }}>Авто</Box>
                              <Box>
                                {collateral.mark || '-'} {collateral.model || '-'}, {collateral.year || '-'}
                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box sx={{ fontStyle: 'italic', color: GRAY_COLORS[600] }}>Нет данных</Box>
                      )}
                    </Box>
                  </Box>
                )}
              </ActionModal>
            </>
          ) : (
            <NotFound />
          )}
        </>
      )}
    </>
  )
}
